/**
 * Renderer — builds all DOM output for Argus Table: table structure,
 * toolbar, and pagination controls.
 *
 * All methods are static since the Renderer holds no state; it transforms
 * configuration objects into DOM elements.
 */

import { resolveField, escapeHtml } from './utils'

export class Renderer {
  /**
   * Render the complete table structure including thead and tbody.
   *
   * @param {Object} config
   * @param {Array}  config.columns        - All column definitions
   * @param {Array}  config.visibleColumns  - Currently visible column definitions
   * @param {Array}  config.pageRows        - Rows for the current page
   * @param {number} config.totalRows       - Total row count (for empty state)
   * @param {number} config.from            - Range start
   * @param {number} config.to              - Range end
   * @param {number} config.currentPage     - Current page number
   * @param {number} config.totalPages      - Total page count
   * @param {Object} config.sortState       - { field, direction }
   * @param {string} config.tableId         - ID for the table element
   * @param {boolean} config.detailView     - Whether detail view is enabled
   * @param {Object} config.detailViewManager - DetailViewManager instance
   * @param {Function} config.onSort        - Callback for sort clicks: onSort(field)
   * @param {Function} config.onDetailToggle - Callback for detail expand/collapse
   * @param {Function} [config.rowAttributesFn] - Optional row attributes function
   * @returns {HTMLTableElement}
   */
  static renderTable(config) {
    const {
      visibleColumns = [],
      pageRows = [],
      sortState = {},
      tableId,
      detailView = false,
      detailViewManager,
      onSort,
      onDetailToggle,
      rowAttributesFn,
    } = config

    const table = document.createElement('table')
    table.className = 'table table-striped table-hover table-bordered'
    table.setAttribute('role', 'grid')
    if (tableId) {
      table.id = tableId
    }

    // ── <thead> ──
    const thead = document.createElement('thead')
    const headerRow = document.createElement('tr')
    headerRow.setAttribute('role', 'row')

    // Detail view expand/collapse icon column header
    if (detailView) {
      const detailTh = document.createElement('th')
      detailTh.setAttribute('role', 'columnheader')
      detailTh.className = 'argus-detail-toggle-header'
      headerRow.appendChild(detailTh)
    }

    visibleColumns.forEach((col) => {
      const th = document.createElement('th')
      th.setAttribute('role', 'columnheader')
      th.textContent = col.title || col.field || ''

      if (col.class) {
        th.className = col.class
      }
      if (col.width) {
        th.style.width = col.width
      }

      // Sort state and click handler
      if (col.sortable) {
        th.style.cursor = 'pointer'
        th.style.whiteSpace = 'nowrap'

        if (sortState.field === col.field && sortState.direction) {
          th.setAttribute(
            'aria-sort',
            sortState.direction === 'asc' ? 'ascending' : 'descending'
          )
          // Inline icon next to title text
          th.textContent += sortState.direction === 'asc' ? ' ▲' : ' ▼'
        } else {
          th.setAttribute('aria-sort', 'none')
          // No icon in default state
        }

        th.addEventListener('click', () => {
          if (typeof onSort === 'function') {
            onSort(col.field)
          }
        })
      } else {
        th.setAttribute('aria-sort', 'none')
      }

      headerRow.appendChild(th)
    })

    thead.appendChild(headerRow)
    table.appendChild(thead)

    // ── <tbody> ──
    const tbody = document.createElement('tbody')

    if (pageRows.length === 0) {
      // Empty state
      const emptyTr = document.createElement('tr')
      const emptyTd = document.createElement('td')
      const colSpan = visibleColumns.length + (detailView ? 1 : 0)
      emptyTd.setAttribute('colspan', String(colSpan))
      emptyTd.textContent = 'No matching records found'
      emptyTr.appendChild(emptyTd)
      tbody.appendChild(emptyTr)
    } else {
      pageRows.forEach((row, index) => {
        const tr = Renderer.renderRow(row, visibleColumns, index, rowAttributesFn)

        // Prepend detail toggle cell if detail view is enabled
        if (detailView && detailViewManager) {
          const toggleTd = document.createElement('td')
          toggleTd.className = 'argus-detail-toggle'

          if (detailViewManager.isExpandable(row)) {
            const icon = document.createElement('span')
            icon.className = 'argus-detail-icon'
            icon.textContent = '▶'
            icon.style.cursor = 'pointer'
            icon.addEventListener('click', () => {
              if (typeof onDetailToggle === 'function') {
                onDetailToggle(index, row, tr)
              }
              // Toggle icon direction
              icon.textContent = detailViewManager.getExpandedIndices().includes(index)
                ? '▼'
                : '▶'
            })
            toggleTd.appendChild(icon)
          }

          tr.insertBefore(toggleTd, tr.firstChild)
        }

        tbody.appendChild(tr)
      })
    }

    table.appendChild(tbody)
    return table
  }

  /**
   * Render a single table row.
   *
   * For each column, resolves the field value from the row object. If the
   * column has a formatter, the formatter is called with (value, row, index)
   * and the result is set as innerHTML. Otherwise, textContent is used for
   * safe rendering.
   *
   * @param {Object}   row              - The data row object
   * @param {Array}    columns          - Visible column definitions
   * @param {number}   index            - Row index within the current page
   * @param {Function} [rowAttributesFn] - Optional function returning attributes for the <tr>
   * @returns {HTMLTableRowElement}
   */
  static renderRow(row, columns, index, rowAttributesFn) {
    const tr = document.createElement('tr')
    tr.setAttribute('role', 'row')

    // Stable row anchor for targeted patch-in-place updates
    if (row && row.id != null) {
      tr.setAttribute('data-argus-row-id', String(row.id))
    }

    // Apply row attributes if provided
    if (typeof rowAttributesFn === 'function') {
      try {
        const attrs = rowAttributesFn(row, index)
        if (attrs && typeof attrs === 'object') {
          Object.keys(attrs).forEach((key) => {
            tr.setAttribute(key, attrs[key])
          })
        }
      } catch (e) {
        console.warn('[ArgusTable Renderer] rowAttributesFn threw an error', e)
      }
    }

    columns.forEach((col) => {
      const td = document.createElement('td')

      // Stable cell anchor for targeted patch-in-place updates
      td.setAttribute('data-argus-col', col.field)

      if (col.class) {
        td.className = col.class
      }
      if (col.width) {
        td.style.width = col.width
      }

      const value = resolveField(row, col.field)
      const formatter = Renderer._resolveFormatter(col.formatter)

      if (formatter) {
        try {
          const formatted = formatter(value, row, index)
          td.innerHTML = formatted
        } catch (e) {
          console.warn(
            `[ArgusTable Renderer] formatter for column "${col.field}" threw an error, falling back to raw value`,
            e
          )
          td.textContent = value != null ? String(value) : ''
        }
      } else if (col.editable) {
        // Render editable cell as a clickable link
        const link = document.createElement('a')
        link.href = '#'
        link.className = 'editable-click'
        link.textContent = value != null ? String(value) : ''
        link.addEventListener('click', (e) => {
          e.preventDefault()
          e.stopPropagation()
          Renderer._openInlineEditor(td, link, row, col, index)
        })
        td.appendChild(link)
      } else {
        td.textContent = Renderer._withPlaceholder(value, col)
      }

      tr.appendChild(td)
    })

    return tr
  }

  /**
   * Patch an existing row's cells in place, without replacing the <tr> or
   * any of its children.
   *
   * For each visible column, locates the cell by its stable
   * `td[data-argus-col="<field>"]` anchor and recomputes the content exactly
   * as `renderRow` would (same formatter / value / text logic). The new
   * content is written only when it differs from what is already in the cell,
   * so unchanged cells — and their child nodes such as buttons — are left
   * untouched and retain their identity.
   *
   * Editable cells manage their own inline-editor child nodes; patching only
   * updates the visible link text and never tears those nodes down. Columns
   * with no matching `<td>` in this row (e.g. the detail-toggle column) are
   * skipped.
   *
   * The `<tr>` node is never detached or replaced, so references held by the
   * driver (and open inline-editor state) remain valid across a patch.
   *
   * @param {HTMLTableRowElement} tr      - The existing row element to patch
   * @param {Object}              row     - The merged data row object
   * @param {Array}               columns - Visible column definitions
   * @param {number}              index   - Row index within the current page
   * @returns {void}
   */
  static patchRow(tr, row, columns, index) {
    if (!tr || !Array.isArray(columns)) {
      return
    }

    columns.forEach((col) => {
      const td = tr.querySelector(
        `td[data-argus-col="${Renderer._cssAttrEscape(col.field)}"]`
      )

      // No matching cell (e.g. detail-toggle column or a column not rendered
      // in this row) — nothing to patch.
      if (!td) {
        return
      }

      const value = resolveField(row, col.field)
      const formatter = Renderer._resolveFormatter(col.formatter)

      if (formatter) {
        let nextHtml
        try {
          nextHtml = formatter(value, row, index)
        } catch (e) {
          // Mirror renderRow's fallback: warn and show the raw value, leaving
          // other cells untouched and never replacing the <tr>.
          console.warn(
            `[ArgusTable Renderer] formatter for column "${col.field}" threw an error during patch, falling back to raw value`,
            e
          )
          const nextText = value != null ? String(value) : ''
          if (td.textContent !== nextText) {
            td.textContent = nextText
          }
          return
        }

        // Only rewrite when the content actually changed — this preserves
        // child button nodes on unchanged formatter-driven cells.
        if (td.innerHTML !== nextHtml) {
          td.innerHTML = nextHtml
          Renderer._reinitTooltips(td)
        }
      } else if (col.editable) {
        // Editable cells own their inline-editor child nodes; patch only the
        // visible link text without destroying them.
        Renderer._patchEditableCell(td, value)
      } else {
        const nextText = Renderer._withPlaceholder(value, col)
        if (td.textContent !== nextText) {
          td.textContent = nextText
        }
      }
    })
  }

  /**
   * Render the toolbar area above the table.
   *
   * Contains: search input, custom buttons, column toggler dropdown,
   * refresh button, and export button.
   *
   * @param {Object}      config
   * @param {boolean}     config.searchEnabled           - Whether to show the search input
   * @param {string}      config.tableId                 - Table ID for aria-controls
   * @param {Array}       config.buttons                 - Custom button definitions [{html, event}]
   * @param {HTMLElement}  config.columnTogglerDropdown   - Pre-rendered dropdown from ColumnToggler
   * @param {boolean}     config.showRefresh             - Whether to show refresh button
   * @param {boolean}     config.showExport              - Whether to show export button
   * @param {Function}    config.onSearch                - Callback for search input
   * @param {Function}    config.onRefresh               - Callback for refresh click
   * @param {Function}    config.onExport                - Callback for export click
   * @returns {HTMLElement}
   */
  static renderToolbar(config) {
    const {
      searchEnabled = true,
      tableId,
      buttons = [],
      columnTogglerDropdown,
      showRefresh = false,
      showExport = false,
      onSearch,
      onRefresh,
      onExport,
    } = config

    const toolbar = document.createElement('div')
    toolbar.className = 'd-flex justify-content-between align-items-center mb-3 flex-wrap gap-2'

    // ── Left side: search ──
    const leftGroup = document.createElement('div')
    leftGroup.className = 'd-flex align-items-center gap-2'

    if (searchEnabled) {
      const searchInput = document.createElement('input')
      searchInput.type = 'text'
      searchInput.className = 'form-control'
      searchInput.placeholder = 'Search...'
      if (tableId) {
        searchInput.setAttribute('aria-controls', tableId)
      }
      searchInput.addEventListener('input', (e) => {
        if (typeof onSearch === 'function') {
          onSearch(e.target.value)
        }
      })
      leftGroup.appendChild(searchInput)
    }

    toolbar.appendChild(leftGroup)

    // ── Right side: buttons, column toggler, refresh, export ──
    const rightGroup = document.createElement('div')
    rightGroup.className = 'd-flex align-items-center gap-2'

    // Custom buttons
    if (Array.isArray(buttons)) {
      buttons.forEach((btnDef) => {
        if (!btnDef) return
        const btnWrapper = document.createElement('span')
        btnWrapper.innerHTML = btnDef.html || ''
        const btn = btnWrapper.firstElementChild || btnWrapper
        if (btnDef.event) {
          btn.addEventListener('click', () => {
            toolbar.dispatchEvent(
              new CustomEvent(btnDef.event, { bubbles: true })
            )
          })
        }
        rightGroup.appendChild(btn)
      })
    }

    // Column toggler dropdown
    if (columnTogglerDropdown instanceof HTMLElement) {
      rightGroup.appendChild(columnTogglerDropdown)
    }

    // Refresh button
    if (showRefresh) {
      const refreshBtn = document.createElement('button')
      refreshBtn.type = 'button'
      refreshBtn.className = 'btn btn-secondary'
      refreshBtn.innerHTML = '&#x21bb;'
      refreshBtn.title = 'Refresh'
      refreshBtn.addEventListener('click', () => {
        if (typeof onRefresh === 'function') {
          onRefresh()
        }
      })
      rightGroup.appendChild(refreshBtn)
    }

    // Export button
    if (showExport) {
      const exportBtn = document.createElement('button')
      exportBtn.type = 'button'
      exportBtn.className = 'btn btn-secondary'
      exportBtn.textContent = 'Export'
      exportBtn.title = 'Export'
      exportBtn.addEventListener('click', () => {
        if (typeof onExport === 'function') {
          onExport()
        }
      })
      rightGroup.appendChild(exportBtn)
    }

    toolbar.appendChild(rightGroup)
    return toolbar
  }

  /**
   * Render pagination controls below the table.
   *
   * Includes previous/next buttons, page size selector dropdown,
   * and a range display showing "from - to of total".
   *
   * @param {Object}   config
   * @param {number}   config.currentPage       - Current page number (1-based)
   * @param {number}   config.totalPages        - Total number of pages
   * @param {number}   config.pageSize          - Current page size
   * @param {Array}    config.pageList          - Selectable page sizes
   * @param {number}   config.totalRows         - Total row count
   * @param {number}   config.from              - Range start
   * @param {number}   config.to                - Range end
   * @param {Function} config.onPageChange      - Callback for page navigation: onPageChange(page)
   * @param {Function} config.onPageSizeChange  - Callback for page size change: onPageSizeChange(size)
   * @param {boolean}  config.serverPagination  - Whether server pagination is active
   * @param {boolean}  config.hasMore           - Whether more pages exist (server pagination)
   * @returns {HTMLElement}
   */
  static renderPagination(config) {
    const {
      currentPage = 1,
      totalPages = 0,
      pageSize = 50,
      pageList = [10, 25, 50, 100],
      totalRows = 0,
      from = 0,
      to = 0,
      onPageChange,
      onPageSizeChange,
      serverPagination = false,
      hasMore = false,
    } = config

    const nav = document.createElement('nav')
    nav.setAttribute('aria-label', 'Table pagination')

    const wrapper = document.createElement('div')
    wrapper.className = 'd-flex justify-content-between align-items-center flex-wrap gap-2'

    // ── Page size selector ──
    const pageSizeGroup = document.createElement('div')
    pageSizeGroup.className = 'd-flex align-items-center gap-2'

    const pageSizeLabel = document.createElement('span')
    pageSizeLabel.textContent = 'Rows per page:'
    pageSizeGroup.appendChild(pageSizeLabel)

    const pageSizeSelect = document.createElement('select')
    pageSizeSelect.className = 'form-select form-select-sm'
    pageSizeSelect.style.width = 'auto'

    pageList.forEach((size) => {
      const option = document.createElement('option')
      option.value = String(size)
      option.textContent = String(size)
      if (size === pageSize) {
        option.selected = true
      }
      pageSizeSelect.appendChild(option)
    })

    pageSizeSelect.addEventListener('change', (e) => {
      if (typeof onPageSizeChange === 'function') {
        onPageSizeChange(Number(e.target.value))
      }
    })

    pageSizeGroup.appendChild(pageSizeSelect)
    wrapper.appendChild(pageSizeGroup)

    // ── Range display ──
    const rangeDisplay = document.createElement('span')
    if (totalRows > 0) {
      rangeDisplay.textContent = `${from} - ${to} of ${totalRows}`
    } else {
      rangeDisplay.textContent = '0 records'
    }
    wrapper.appendChild(rangeDisplay)

    // ── Pagination buttons ──
    const paginationUl = document.createElement('ul')
    paginationUl.className = 'pagination mb-0'

    // Previous button
    const prevLi = document.createElement('li')
    prevLi.className = 'page-item' + (currentPage <= 1 ? ' disabled' : '')
    const prevLink = document.createElement('button')
    prevLink.type = 'button'
    prevLink.className = 'page-link'
    prevLink.textContent = 'Previous'
    if (currentPage > 1) {
      prevLink.addEventListener('click', () => {
        if (typeof onPageChange === 'function') {
          onPageChange(currentPage - 1)
        }
      })
    }
    prevLi.appendChild(prevLink)
    paginationUl.appendChild(prevLi)

    // Page number buttons (client pagination only)
    if (!serverPagination && totalPages > 0) {
      const pages = Renderer._getPageNumbers(currentPage, totalPages)
      pages.forEach((page) => {
        const li = document.createElement('li')
        li.className = 'page-item' + (page === currentPage ? ' active' : '')
        const link = document.createElement('button')
        link.type = 'button'
        link.className = 'page-link'
        link.textContent = String(page)
        if (page !== currentPage) {
          link.addEventListener('click', () => {
            if (typeof onPageChange === 'function') {
              onPageChange(page)
            }
          })
        }
        li.appendChild(link)
        paginationUl.appendChild(li)
      })
    }

    // Next button
    const nextLi = document.createElement('li')
    const nextDisabled = serverPagination
      ? !hasMore
      : currentPage >= totalPages
    nextLi.className = 'page-item' + (nextDisabled ? ' disabled' : '')
    const nextLink = document.createElement('button')
    nextLink.type = 'button'
    nextLink.className = 'page-link'
    nextLink.textContent = 'Next'
    if (!nextDisabled) {
      nextLink.addEventListener('click', () => {
        if (typeof onPageChange === 'function') {
          onPageChange(currentPage + 1)
        }
      })
    }
    nextLi.appendChild(nextLink)
    paginationUl.appendChild(nextLi)

    wrapper.appendChild(paginationUl)
    nav.appendChild(wrapper)
    return nav
  }

  // ── Private helpers ──────────────────────────────────────────────

  /**
   * Resolve a formatter from a column definition.
   *
   * Accepts either a direct function reference or a string name to look up
   * on `window`. Returns null if no valid formatter is found, logging a
   * warning for string names that don't resolve.
   *
   * @param {string|Function|undefined} formatter
   * @returns {Function|null}
   * @private
   */
  static _resolveFormatter(formatter) {
    if (typeof formatter === 'function') {
      return formatter
    }

    if (typeof formatter === 'string' && formatter !== '') {
      const windowFn = window[formatter]
      if (typeof windowFn === 'function') {
        return windowFn
      }
      console.warn(
        `[ArgusTable Renderer] formatter "${formatter}" not found on window, rendering raw value`
      )
    }

    return null
  }

  /**
   * Resolve the display text for a non-formatter cell, applying the
   * deferred-cell placeholder convention.
   *
   * Rule (design.md → Algorithmic Pseudocode → `_withPlaceholder`,
   * Requirement 5):
   *   - `null`/`undefined` → the column's `placeholder` token (e.g. `…`) when
   *     one is defined, otherwise `''`.
   *   - any real value (including `0` and `""`) → `String(value)`; the
   *     placeholder is NEVER produced for a real value, so a real `0` renders
   *     as `"0"` and a real `""` renders as `""`.
   *
   * @param {*}      value - The resolved field value for the cell
   * @param {Object} col   - The column definition (may carry `placeholder`)
   * @returns {string}
   * @private
   */
  static _withPlaceholder(value, col) {
    if (value == null) {
      return col && col.placeholder != null ? String(col.placeholder) : ''
    }
    return String(value)
  }

  /**
   * Patch the visible value of an editable cell in place.
   *
   * Editable cells render as a clickable `a.editable-click` link and may, while
   * being edited, contain an inline-editor subtree. Patching must update the
   * displayed value WITHOUT destroying those child nodes. If the cell is
   * currently being edited (an `.editable-inline` editor is present) the visible
   * value is left to the editor and only the backing link text is updated so the
   * correct value shows once editing closes.
   *
   * If no link exists yet (e.g. the cell was rendered empty), the text content
   * is updated directly as a fallback.
   *
   * @param {HTMLElement} td    - The editable table cell
   * @param {*}           value - The resolved field value
   * @returns {void}
   * @private
   */
  static _patchEditableCell(td, value) {
    const nextText = value != null ? String(value) : ''
    const link = td.querySelector('a.editable-click')

    if (link) {
      if (link.textContent !== nextText) {
        link.textContent = nextText
      }
      return
    }

    // No link present and not mid-edit — update raw text as a fallback without
    // disturbing any other children.
    if (!td.querySelector('.editable-inline') && td.textContent !== nextText) {
      td.textContent = nextText
    }
  }

  /**
   * (Re)initialize Bootstrap tooltips for elements inside a freshly-written
   * cell.
   *
   * Invoked after a formatter-driven cell's `innerHTML` is rewritten during a
   * patch, so controls that are newly added (e.g. a Repair button that appears
   * when a policy goes stale) or re-rendered get a working tooltip just like a
   * fully rendered row. Every `[data-bs-toggle="tooltip"]` element within the
   * cell is bound via `bootstrap.Tooltip.getOrCreateInstance`, which is
   * idempotent so already-initialized controls are not double-bound.
   *
   * Guarded on the presence of the Bootstrap tooltip library; when it is
   * absent this no-ops so the patch path never throws.
   *
   * @param {HTMLElement} td - The cell whose tooltip-bearing elements to init
   * @returns {void}
   * @private
   */
  static _reinitTooltips(td) {
    if (!td || !window.bootstrap || !window.bootstrap.Tooltip) {
      return
    }

    const elements = td.querySelectorAll('[data-bs-toggle="tooltip"]')
    elements.forEach((el) => {
      window.bootstrap.Tooltip.getOrCreateInstance(el)
    })
  }

  /**
   * Escape a value for safe use inside a double-quoted CSS attribute selector.
   *
   * Column field names are developer-defined, but may contain characters that
   * are significant in a selector; escaping keeps `querySelector` robust.
   *
   * @param {string} value - The attribute value to escape
   * @returns {string}
   * @private
   */
  static _cssAttrEscape(value) {
    return String(value == null ? '' : value).replace(/["\\]/g, '\\$&')
  }

  /**
   * Open an inline editor for an editable cell.
   *
   * Replaces the clickable link with an input field and save/cancel buttons.
   * On save, dispatches a custom 'argus-table:editable-save' event with the
   * field, old value, new value, row data, and row index.
   *
   * @param {HTMLElement} td - The table cell
   * @param {HTMLElement} link - The editable-click link element
   * @param {Object} row - The row data object
   * @param {Object} col - The column definition
   * @param {number} index - The row index
   * @private
   */
  static _openInlineEditor(td, link, row, col, index) {
    const oldValue = link.textContent
    const field = col.field

    // Create editor container
    const editor = document.createElement('div')
    editor.className = 'editable-inline d-flex gap-1 align-items-center'

    const input = document.createElement('input')
    input.type = 'text'
    input.className = 'form-control form-control-sm editable-input'
    input.value = oldValue
    input.style.minWidth = '80px'

    const saveBtn = document.createElement('button')
    saveBtn.type = 'button'
    saveBtn.className = 'btn btn-sm btn-primary editable-submit'
    saveBtn.innerHTML = '&#x2713;'

    const cancelBtn = document.createElement('button')
    cancelBtn.type = 'button'
    cancelBtn.className = 'btn btn-sm btn-secondary editable-cancel'
    cancelBtn.innerHTML = '&#x2717;'

    const buttonsDiv = document.createElement('div')
    buttonsDiv.className = 'editable-buttons d-flex gap-1'
    buttonsDiv.appendChild(saveBtn)
    buttonsDiv.appendChild(cancelBtn)

    editor.appendChild(input)
    editor.appendChild(buttonsDiv)

    // Replace link with editor
    link.style.display = 'none'
    td.appendChild(editor)
    input.focus()
    input.select()

    const close = () => {
      editor.remove()
      link.style.display = ''
    }

    const save = () => {
      const newValue = input.value
      if (newValue !== oldValue) {
        // Dispatch event for the controller to handle the API call
        td.closest('table')?.dispatchEvent(new CustomEvent('argus-table:editable-save', {
          bubbles: true,
          detail: { field, oldValue, newValue, row, index }
        }))
        link.textContent = newValue
      }
      close()
    }

    saveBtn.addEventListener('click', (e) => { e.stopPropagation(); save() })
    cancelBtn.addEventListener('click', (e) => { e.stopPropagation(); close() })
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); save() }
      if (e.key === 'Escape') { e.preventDefault(); close() }
    })
  }

  /**
   * Compute a compact set of page numbers to display in pagination.
   *
   * Shows first, last, current, and up to 2 neighbours around current.
   * Gaps are not represented (the caller can add ellipsis if desired).
   *
   * @param {number} current    - Current page (1-based)
   * @param {number} totalPages - Total number of pages
   * @returns {Array<number>}
   * @private
   */
  static _getPageNumbers(current, totalPages) {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }

    const pages = new Set()
    pages.add(1)
    pages.add(totalPages)

    for (let i = current - 2; i <= current + 2; i++) {
      if (i >= 1 && i <= totalPages) {
        pages.add(i)
      }
    }

    return Array.from(pages).sort((a, b) => a - b)
  }
}
